import { Api } from '@/utils/api';
import { buildPaginationParams } from '@/utils/queries/buildPaginationParams';
import { normaliseApiNotification } from '@/utils/inAppNotification';
import type {
  InAppNotificationApiRecord,
  InAppNotificationsMeta,
  InAppNotificationsPageData,
  InAppNotificationsPageParams,
} from '@/types/inAppNotification';

/**
 * One numbered page of the full notification history.
 *
 * Sending `page` switches the endpoint into its paginated form, the only one that
 * accepts `filter[seen]` and `filter[reference_type]` and reports
 * `meta.pagination`. Without it the endpoint answers the panel's `limit` contract
 * instead.
 *
 * Unlike `fetchInAppNotifications` this does not consult the live notification
 * buffer: a page is a fixed slice, and a notification arriving while it loads
 * belongs at the top of page one, not spliced into whichever page is open.
 */
export const fetchInAppNotificationsPage = async ({
  seenFilter,
  referenceType,
  page,
  perPage,
}: InAppNotificationsPageParams): Promise<InAppNotificationsPageData> => {
  const response = await Api.get<
    InAppNotificationApiRecord[],
    null,
    InAppNotificationsMeta
  >('/api/notifications/in_app', {
    params: {
      page: buildPaginationParams({ page, perPage }),
      filter: {
        // A string, because the API client's serializer drops every falsy value
        // and a boolean `false` would silently turn the filter off.
        seen: seenFilter === 'unseen' ? 'false' : undefined,
        referenceType: referenceType ?? undefined,
      },
    },
  });

  const notifications = (response.data ?? [])
    .map(normaliseApiNotification)
    .filter((notification) => notification !== null);

  return {
    notifications,
    pagination: response.meta?.pagination ?? {
      pageNumber: page,
      pageSize: perPage,
      totalEntries: notifications.length,
      totalPages: notifications.length > 0 ? page : 0,
    },
  };
};
